import React, {ChangeEvent, KeyboardEvent, ReactElement} from "react";
import {v4 as uuid} from "uuid";

import {logErrorToSentry} from "/src/utils/sentry";
import {
  AddressObjectType,
  FormGroupBase,
  PlacesAPIPrediction,
  PlacesAPIResponse,
} from "/src/types";
import KEY from "/src/constants/keyboard-keydown-key";
import {css} from "/src/utils/utils";

import Label from "../../label/Label";
import ErrorMessage from "../../error-message/ErrorMessage";
import DropdownMenu from "/src/components/dropdown-menu/DropdownMenu";
import Icon from "/src/components/icon/Icon";

import {
  resetPhysicalObject,
  resetMailingObject,
  getFullAddressDetails,
} from "/src/utils/forms";

import ChevronRightIcon from "/src/svg/chevron-right.svg";

import * as styles from "./address-auto-complete.module.scss";

interface AddressAutoCompleteProps extends FormGroupBase {
  placeholder?: string;
  transparentBg?: boolean;
  disableError?: boolean;
  onChange: (change: string | AddressObjectType) => unknown;
  addressType: "mailing" | "physical";
  valueForAddressField?: AddressObjectType;
  sameAsMailing: boolean;
  disableAutoDuplication?: boolean;
  hideDropdownButton?: boolean;
  minLettersForSearch?: number;
}

const AddressAutoComplete = (props: AddressAutoCompleteProps): ReactElement => {
  const {
    placeholder = "",
    label = "",
    required = false,
    name = "",
    errorMessage = "",
    disabled = false,
    darkMode = false,
    transparentBg = false,
    disableError = false,
    onChange,
    addressType,
    valueForAddressField,
    sameAsMailing,
    status,
    disableAutoDuplication = false,

    hideDropdownButton = false,
    minLettersForSearch = 0,
  } = props;

  const [sessionToken, setSessionToken] = React.useState<string>(uuid());
  const refresh = () => setSessionToken(uuid());

  // Todo[DOTORG-2630]: Address the root cause instead of using a ref workaround
  // TODO[FM-440]: Temp fix to allow form to work again

  const prevSameAsMailing = React.useRef(sameAsMailing);
  const physicalAddressBackup = React.useRef<Partial<AddressObjectType> | null>(
    null
  );
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Toggle logic for mailing address when sameAsMailing state changes
  // This is bandaid for the loop issue.
  React.useEffect(() => {
    // Check if sameAsMailing has changed since the last render.
    const hasChanged = prevSameAsMailing.current !== sameAsMailing;

    if (hasChanged) {
      // Update the ref so that it holds the current value for the next render.
      prevSameAsMailing.current = sameAsMailing;

      let newAddressObject;
      const shouldMatchAddresses =
        sameAsMailing &&
        addressType === "physical" &&
        valueForAddressField != null;

      if (shouldMatchAddresses) {
        // backup
        physicalAddressBackup.current = {
          physical_street_address:
            valueForAddressField.physical_street_address ?? "",
          physical_street_address2:
            valueForAddressField.physical_street_address2 ?? "",
          physical_city: valueForAddressField.physical_city ?? "",
          physical_country: valueForAddressField.physical_country ?? "",
          physical_state: valueForAddressField.physical_state ?? "",
          physical_postal_code: valueForAddressField.physical_postal_code ?? "",
          physicalAddressSet: valueForAddressField.physicalAddressSet ?? false,
        };

        newAddressObject = {
          ...valueForAddressField,
          physical_street_address: valueForAddressField.address_one,
          physical_street_address2: valueForAddressField.address_two,
          physical_city: valueForAddressField.city,
          physical_country: valueForAddressField.country,
          physical_state: valueForAddressField.state,
          physical_postal_code: valueForAddressField.zip,
          physicalAddressSet: true,
        };
      } else {
        // restore physical address back to previous values
        if (physicalAddressBackup.current) {
          newAddressObject = {
            ...valueForAddressField,
            physical_street_address:
              physicalAddressBackup.current.physical_street_address,
            physical_street_address2:
              physicalAddressBackup.current.physical_street_address2,
            physical_city: physicalAddressBackup.current.physical_city,
            physical_country: physicalAddressBackup.current.physical_country,
            physical_state: physicalAddressBackup.current.physical_state,
            physical_postal_code:
              physicalAddressBackup.current.physical_postal_code,
            physicalAddressSet:
              physicalAddressBackup.current.physicalAddressSet,
          };
        }

        // clear the backup now that its restored it.
        physicalAddressBackup.current = null;
      }

      if (onChangeRef.current && newAddressObject)
        onChangeRef.current(newAddressObject);
    }
  }, [sameAsMailing, valueForAddressField, addressType]);

  React.useEffect(() => {
    if (
      disableAutoDuplication ||
      addressType !== "mailing" ||
      !valueForAddressField ||
      valueForAddressField.physicalAddressSet
    ) {
      return;
    }
    const newAddressObject = {
      ...valueForAddressField,
      physical_street_address: valueForAddressField.address_one,
      physical_street_address2: valueForAddressField.address_two,
      physical_city: valueForAddressField.city,
      physical_country: valueForAddressField.country,
      physical_state: valueForAddressField.state,
      physical_postal_code: valueForAddressField.zip,
      physicalAddressSet: true,
    };

    if (onChangeRef.current) {
      onChangeRef.current(newAddressObject);
    }
  }, [valueForAddressField, addressType, disableAutoDuplication]);

  const [predictions, setPredictions] = React.useState<
    Array<PlacesAPIPrediction>
  >([]);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [selectedAddress, setSelectedAddress] =
    React.useState<null | PlacesAPIPrediction>(null);

  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const [focusedListItem, setFocusedListItem] = React.useState<number>(-1);

  const $root = React.useRef<null | HTMLDivElement>(null);
  const $input = React.useRef<null | HTMLInputElement>(null);

  // status !== "" check so that theres no errors flashing on initial state to match rest of fields
  const isInvalid = status !== "" && selectedAddress === null;
  const isValid = status !== "" && selectedAddress !== null;

  const labelId = `${name}_label`;
  const triggerId = `${name}_trigger`;
  const itemListId = `${name}_list`;
  const inputId = `${name}_input`;

  // whether we should forced bottom anchor on
  // dropdown component
  // on touch screen device, we should just fixed
  // the menu anchoring at
  // the bottom, to fixed erratic dropdown position
  // on input focus, as the keyboard show up
  // ref: https://mui.com/material-ui/react-autocomplete/#combo-box for behaviour
  const isTouchDevice =
    typeof window !== "undefined" &&
    window.matchMedia("(any-pointer:coarse)").matches;

  const hasMinLettersForSearch = React.useCallback(() => {
    if ($input.current) {
      return $input.current.value.length >= minLettersForSearch;
    }

    return false;
  }, [minLettersForSearch]);

  const onInputFocus = () => {
    if (!isOpen && hasMinLettersForSearch()) {
      setIsOpen(true);
    }
  };

  const onInputKeydown = (_e: KeyboardEvent) => {
    switch (_e.key) {
      case KEY.ENTER:
        _e.preventDefault();
        if (focusedListItem !== -1) {
          selectAddress(predictions[focusedListItem]);
        }
        break;
      case KEY.TAB:
      case KEY.ESC:
        setIsOpen(false);
        setFocusedListItem(-1);
        break;
      case KEY.UP:
        {
          _e.preventDefault();
          const tempIdx = focusedListItem - 1;

          focusListItemByIdx(tempIdx);

          setIsOpen(true);
        }
        break;
      case KEY.DOWN:
        {
          _e.preventDefault();
          const tempIdx = focusedListItem + 1;

          focusListItemByIdx(tempIdx);

          setIsOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const focusListItemByIdx = (idx: number) => {
    if (idx > -1 && idx < predictions.length) {
      setFocusedListItem(idx);
    }
  };

  const selectAddress = async (address: PlacesAPIPrediction) => {
    setIsOpen(false);

    setSelectedAddress(address);

    if ($input.current) {
      $input.current.value = address.description;
    }
    try {
      const addressDetails = await getFullAddressDetails(
        address.place_id,
        sessionToken,
        addressType,
        sameAsMailing
      );
      if (onChange) {
        let newAddressObject;
        if (valueForAddressField != null) {
          newAddressObject = {...valueForAddressField, ...addressDetails};
        } else if (addressType === "mailing")
          newAddressObject = {...addressDetails, ...resetPhysicalObject};
        else newAddressObject = {...addressDetails, ...resetMailingObject};
        onChange(newAddressObject);
      }
    } catch (err: any) {
      logErrorToSentry(err);
    }

    setPredictions([]);
    refresh();
  };

  const onListItemClick = (address: PlacesAPIPrediction) => {
    selectAddress(address);
  };

  const onListItemEnter = (idx: number) => {
    setFocusedListItem(idx);
  };

  const onListItemLeave = () => {
    setFocusedListItem(-1);
  };

  const handleAddressSearch = async (event: ChangeEvent) => {
    const {value} = event.target as HTMLInputElement;
    setSearchValue(value);

    let predictions: Array<PlacesAPIPrediction> = [];

    if (value !== "") {
      try {
        const req = await fetch("/.netlify/functions/places-autocomplete-api", {
          body: JSON.stringify({
            value,
            sessionToken,
          }),
          method: "POST",
        });

        const res = (await req.json()) as PlacesAPIResponse;
        predictions = res.predictions;
      } catch (err) {
        logErrorToSentry(err as Error);
      }
    }

    setPredictions(predictions);
  };

  const onInputChange = (e: ChangeEvent) => {
    if (!isOpen && hasMinLettersForSearch()) {
      setIsOpen(true);
    }

    setSelectedAddress(null);

    if (onChange) {
      if (valueForAddressField === null) {
        onChange({
          ...resetMailingObject,
          ...resetPhysicalObject,
        });
      } else if (addressType === "mailing") {
        onChange({
          ...valueForAddressField,
          ...resetMailingObject,
        });
      } else {
        onChange({
          ...valueForAddressField,
          ...resetPhysicalObject,
        });
      }
    }

    handleAddressSearch(e);
  };

  React.useEffect(() => {
    if (!isOpen) {
      setFocusedListItem(-1);
    }
    return () => {}; // satisfies eslint's consistent-return rule
  }, [isOpen]);

  return (
    <div
      data-testid="address-auto-complete"
      className={css(
        styles.addressAutoComplete,
        darkMode ? styles.addressAutoCompleteDark : "",
        transparentBg ? styles.addressAutoCompleteTransparent : "",
        isOpen ? styles.addressAutoCompleteOpen : "",
        isOpen ? styles.addressAutoCompleteFocus : "",
        isInvalid ? styles.addressAutoCompleteError : "",
        isValid ? styles.addressAutoCompleteSuccess : "",
        disabled ? styles.addressAutoCompleteDisabled : ""
      )}
    >
      <Label
        darkMode={darkMode}
        disabled={disabled}
        label={label}
        required={required}
        id={labelId}
      />

      <div ref={$root} className={css(styles.addressAutoComplete__el)}>
        <div className={css(styles.addressAutoComplete__inputWrapper)}>
          <input
            data-lpignore="true"
            autoComplete="off"
            defaultValue={
              selectedAddress !== null
                ? selectedAddress.description
                : searchValue
            }
            disabled={disabled}
            type="address"
            id={inputId}
            ref={$input}
            onChange={onInputChange}
            onKeyDown={onInputKeydown}
            onFocus={onInputFocus}
            placeholder={placeholder}
            className={css(styles.addressAutoComplete__input)}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={itemListId}
          />
          {!hideDropdownButton && (
            <button
              className={css("p", styles.addressAutoComplete__btn)}
              tabIndex={-1}
              type="button"
              aria-expanded={isOpen}
              aria-controls={itemListId}
              id={triggerId}
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            >
              <Icon
                role="presentation"
                svg={ChevronRightIcon}
                classes={styles.addressAutoComplete__icon}
              />
            </button>
          )}
        </div>

        <DropdownMenu
          bottomStyles={{
            borderRadius: "0 0 4px 4px",
            borderBottom: "2px solid",
            borderBottomColor: "inherit",
          }}
          classes={styles.addressAutoComplete__dropdown}
          forceBottomAnchor={isTouchDevice}
          maxHeight={344}
          offsetY={-4}
          open={isOpen}
          parent={$root}
          restrictWidth
          toggle={(val: boolean) => {
            // if input is in focus, and val is false, do nothing
            if (document.activeElement === $input.current && !val) {
              return;
            }
            setIsOpen(val);
          }}
          topStyles={{borderRadius: "4px 4px 0 0", borderTop: "2px solid"}}
        >
          <ul
            data-testid="address-auto-complete-listbox"
            className={css("p", styles.addressAutoComplete__list)}
            id={itemListId}
            tabIndex={-1}
            role="listbox"
            aria-hidden={!isOpen}
            aria-labelledby={labelId}
          >
            {predictions.map((option: PlacesAPIPrediction, i: number) => {
              const selected =
                selectedAddress !== null &&
                selectedAddress.place_id === option.place_id;
              const listIsFocus = i === focusedListItem;
              return (
                <li
                  onFocus={() => onListItemEnter(i)}
                  onBlur={onListItemLeave}
                  // eslint-disable-next-line no-underscore-dangle
                  key={`${name}-${addressType}-${option.place_id}-key`}
                  onClick={() => onListItemClick(option)}
                  className={css(
                    styles.addressAutoComplete__item,
                    listIsFocus ? styles.addressAutoComplete__itemFocus : "",
                    selected ? styles.addressAutoComplete__itemSelected : ""
                  )}
                  id={option.place_id}
                  role="option"
                  aria-selected={selected}
                >
                  {option.description}
                </li>
              );
            })}
          </ul>
        </DropdownMenu>
      </div>
      {!disableError && (
        <ErrorMessage message={errorMessage} visible={isInvalid} />
      )}
    </div>
  );
};

export default AddressAutoComplete;
